import { Region } from '@components/common/constants';

import { parseReturnOrigins } from './auth/appRouter/oauthReturn';

export const BACKEND_BASE_URL = {
  [Region.US]:
    process.env.REACT_APP_BACKEND_URL ||
    process.env.REACT_APP_BACKEND_URL_US ||
    'http://localhost:5050',
  [Region.EU]: process.env.REACT_APP_BACKEND_URL_EU || 'http://localhost:5051',
  [Region.IN]: process.env.REACT_APP_BACKEND_URL_IN || 'http://localhost:5052',
};

export const WEB_APP_URL = process.env.REACT_APP_WEB_URL || 'http://localhost:3000';

/**
 * Workspaces created on or after this date get RudderAI guided onboarding.
 * Older workspaces were set up without it and are never guided. Override per
 * environment with REACT_APP_RUDDER_AI_ONBOARDING_FROM (an ISO date).
 */
export const RUDDER_AI_ONBOARDING_FROM = new Date(
  process.env.REACT_APP_RUDDER_AI_ONBOARDING_FROM || '2026-09-10',
);

/**
 * First-party origins the `/oauth` page may hand a provider callback on to (comma-separated exact
 * origins, e.g. `https://lookout.rudderstack.com`). Empty — the default — means the page behaves
 * exactly as before: same-origin BroadcastChannel only. See `src/auth/appRouter/oauthReturn.ts`.
 */
export const OAUTH_RETURN_ORIGINS = parseReturnOrigins(process.env.REACT_APP_OAUTH_RETURN_ORIGINS);

/**
 * Rudder Lookout's origin, used by the /authorize consent page to decide whether a
 * request is Lookout's. Deliberately has no default: the hostnames belong to the
 * deploy pipeline, not to application code, and an unset value simply means no
 * caller is ever branded.
 */
export const LOOKOUT_URL = process.env.REACT_APP_LOOKOUT_URL;

const US_ACTIVATION_API_URL = process.env.REACT_APP_US_ACTIVATION_API || 'http://localhost:5110';

export const ACTIVATION_HOST_URL = {
  [Region.US]: US_ACTIVATION_API_URL,
  [Region.EU]: process.env.REACT_APP_EU_ACTIVATION_API || US_ACTIVATION_API_URL,
  [Region.IN]: process.env.REACT_APP_IN_ACTIVATION_API || US_ACTIVATION_API_URL,
};

export const disableCognito = process.env.REACT_APP_DISABLE_COGNITO === 'true';
export const disableAnalytics = process.env.REACT_APP_DISABLE_ANALYTICS === 'true' || false;
export const disableSSO = process.env.REACT_APP_DISABLE_SSO === 'true';

// Calendly scheduling link opened by the in-product "Contact sales" CTAs.
// No default — the scheduler stays disabled until this is set per environment.
export const calendlySchedulingUrl = process.env.REACT_APP_CALENDLY_SCHEDULING_URL;

export const DATADOG_APP_ID = process.env.REACT_APP_DATADOG_APP_ID || '';
export const DATADOG_CLIENT_TOKEN = process.env.REACT_APP_DATADOG_CLIENT_VERSION || '';
export const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

export const TABLE_VIRTUALIZATION_THRESHOLD = parseInt(
  process.env.REACT_APP_TABLE_VIRTUALIZATION_THRESHOLD || '80',
  10,
);

// ts-prune-ignore-next
export const userPoolMCPClientId = process.env.REACT_APP_AWS_USER_POOL_MCP_CLIENT_ID || '';

export const awsConfig = {
  Auth: {
    region: process.env.REACT_APP_AWS_REGION || '',
    userPoolId: process.env.REACT_APP_AWS_USER_POOL_ID || '',
    // We need the mcp client id of the mcp
    userPoolWebClientId: process.env.REACT_APP_AWS_USER_POOL_WEB_CLIENT_ID || '',
    oauth: {
      domain: process.env.REACT_APP_COGNITO_DOMAIN_ENDPOINT || '',
      scope: ['email', 'profile', 'openid'],
      redirectSignIn: `${WEB_APP_URL}/signup`,
      redirectSignOut: `${WEB_APP_URL}/signup`,
      responseType: 'code',
    },
  },
};

export const SLACK_APP_CLIENT_ID =
  process.env.REACT_APP_SLACK_APP_CLIENT_ID || '611722233910.6013277261767';

export const LIVE_EVENT_CAPTURE_TIME_IN_MIN = parseInt(
  process.env.REACT_APP_LIVE_EVENT_CAPTURE_TIME_IN_MIN || '5',
  10,
);

export const ASYNC_API_MAX_POLL_INTERVAL = parseInt(
  process.env.REACT_APP_ASYNC_API_MAX_POLL_INTERVAL || '10000',
  10,
);

export const ASYNC_API_TIMEOUT = parseInt(process.env.REACT_APP_ASYNC_API_TIMEOUT || '660000', 10);

export const LOCAL_FEATURE_FLAGS = {
  VDM_NEXT: process.env.REACT_APP_VDM_NEXT || 'false',
  RS360_ENABLE_WHT: process.env.REACT_APP_RS360_ENABLE_WHT || 'false',
  ENABLE_PROFILES_UIV1: process.env.REACT_APP_ENABLE_PROFILES_UIV1 || 'false',
  ENABLE_COHORT_AUDIENCE: process.env.REACT_APP_ENABLE_COHORT_AUDIENCE || 'false',
  ENABLE_PROFILES_COPILOT: process.env.REACT_APP_ENABLE_PROFILES_COPILOT || 'false',
  ENABLE_DATA_GRAPH_AUDIENCES: process.env.REACT_APP_ENABLE_DATA_GRAPH_AUDIENCES || 'false',
  ENABLE_HS_AUDIENCE_DESTINATION: process.env.REACT_APP_ENABLE_HS_AUDIENCE_DESTINATION || 'false',
  ENABLE_MARKETO_AUDIENCE_DESTINATION:
    process.env.REACT_APP_ENABLE_MARKETO_AUDIENCE_DESTINATION || 'false',
  ENABLE_INLINE_GET_STARTED: process.env.REACT_APP_ENABLE_INLINE_GET_STARTED || 'false',
  ENABLE_GAINSIGHT_PX_AUDIENCE_DESTINATION:
    process.env.REACT_APP_ENABLE_GAINSIGHT_PX_AUDIENCE_DESTINATION || 'false',
  ENABLE_DATA_GRAPH: process.env.REACT_APP_ENABLE_DATA_GRAPH || 'false',
  ENABLE_CODE_SERVER_TOKEN_EXCHANGE:
    process.env.REACT_APP_ENABLE_CODE_SERVER_TOKEN_EXCHANGE || 'false',
  ENABLE_NEW_PERMISSIONS_MODEL: process.env.REACT_APP_ENABLE_NEW_PERMISSIONS_MODEL || 'false',
  ENABLE_NEW_PERMISSIONS_MODEL_ENFORCEMENT:
    process.env.REACT_APP_ENABLE_NEW_PERMISSIONS_MODEL_ENFORCEMENT || 'false',
  ENABLE_TRANSFORMATIONS_TESTS: process.env.REACT_APP_ENABLE_TRANSFORMATIONS_TESTS || 'false',
  ENABLE_PROFILES_OUTPUT_SCHEMA: process.env.REACT_APP_ENABLE_PROFILES_OUTPUT_SCHEMA || 'false',
  ENABLE_TOTP_MFA: process.env.REACT_APP_ENABLE_TOTP_MFA || 'false',
  ENABLE_RUDDER_AI_WEB_CHAT: process.env.REACT_APP_ENABLE_RUDDER_AI_WEB_CHAT || 'false',
  ENABLE_CUSTOMER_IO_IN_APP: process.env.REACT_APP_ENABLE_CUSTOMER_IO_IN_APP || 'false',
  ENABLE_HIGH_EVENT_VOLUME_ALERT: process.env.REACT_APP_ENABLE_HIGH_EVENT_VOLUME_ALERT || 'false',
  ENABLE_RETL_FAILED_RECORDS: process.env.REACT_APP_ENABLE_RETL_FAILED_RECORDS || 'false',
  ENABLE_GROUP_MAPPING: process.env.REACT_APP_ENABLE_GROUP_MAPPING || 'false',
  ENABLE_LOOKOUT_SHARED_AUTH_SESSION:
    process.env.REACT_APP_ENABLE_LOOKOUT_SHARED_AUTH_SESSION || 'false',
};

export const amplitudeEngagementConfig = {
  apiKey: process.env.REACT_APP_AMPLITUDE_ENGAGEMENT_API_KEY || '',
};

// Customer.io in-app messaging (Gist). The site ID comes from the Journeys
// workspace (Settings > Account Settings > API Credentials). No default — the
// integration stays inert until this is set per environment, so a missing value
// is a safe "off" rather than a broken SDK boot.
export const customerIoInAppConfig = {
  siteId: process.env.REACT_APP_CUSTOMERIO_SITE_ID || '',
};

export const flagsmithConfig = {
  environmentKey: process.env.REACT_APP_FLAGSMITH_ENV_KEY || '',
};

export const freeTierBlockedEmailDomains = (
  process.env.REACT_APP_FREE_TIER_BLOCKED_EMAIL_DOMAINS || ''
)
  .split(',')
  .map((s) => s.trim())
  .filter((i) => i);

export const IS_ADMIN_WEB_APP = process.env.REACT_APP_IS_ADMIN_WEB_APP === 'true' || false;
